/**
 * The shelf.
 *
 * Everything the group owns a box of, art forward, in a grid you scroll for
 * pleasure rather than to find a row. Three things about it are rules, not
 * layout decisions:
 *
 *  - There are no aspect values on a tile, and no summary of them. Aspects are
 *    descriptions, and a grid of descriptions compressed into a badge is how a
 *    "Weight: 7" leaderboard gets born. Aspect-value filters are backlog item 1;
 *    there is deliberately no greyed control hinting at them either.
 *  - The one number here is the headline, rendered by `Headline` so it cannot
 *    appear without its `n`, and rendered as words when the group has never
 *    finished a review. Unrated is not zero.
 *  - The art is never touched. `BoxArt` reserves its square and asks BGG's CDN
 *    for the rendition it needs; this screen only lets the square be fluid, and
 *    `Attribution` ships once at the foot because box art is on this page.
 *
 * Sort and group live in the hash query, so a shelf view is a link somebody can
 * send. They are read straight off the `query` prop rather than mirrored into
 * state: `navigate(..., { replace: true })` re-renders the route with the new
 * query, which makes the URL the single copy of this setting.
 */

import { useMemo } from "react";
import type { ReactNode } from "react";

import { Tile } from "../../components/Tile.tsx";
import { Link, navigate } from "../../main.tsx";
import type { ScreenProps } from "../../main.tsx";
import {
  Attribution,
  BoxArt,
  EmptyState,
  ScorePair,
  ScreenHeader,
  Segmented,
  SyncLine,
} from "./components.tsx";
import {
  GROUP_LABELS,
  SORT_LABELS,
  groupGames,
  relativeDay,
  summariseGames,
  type GameSummary,
  type GroupMode,
  type SortMode,
} from "./data.ts";
import { useLibrary } from "./useLibrary.ts";

import "./catalog-grid.css";

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

const DEFAULT_SORT: SortMode = "recent";
const DEFAULT_GROUP: GroupMode = "none";

/** Declared rather than derived from the label maps, because order is a choice. */
const SORT_ORDER: readonly SortMode[] = ["recent", "plays", "headline", "title"];
const GROUP_ORDER: readonly GroupMode[] = ["none", "played", "players", "weight", "title"];

const SORT_OPTIONS = SORT_ORDER.map((value) => ({ value, label: SORT_LABELS[value] }));
const GROUP_OPTIONS = GROUP_ORDER.map((value) => ({ value, label: GROUP_LABELS[value] }));

/** A stale or hand-edited link must land on a real shelf, not on `undefined`. */
function readMode<T extends string>(
  raw: string | null,
  labels: Readonly<Record<T, string>>,
  fallback: T,
): T {
  return raw !== null && Object.hasOwn(labels, raw) ? (raw as T) : fallback;
}

/** Defaults stay out of the URL, so the plain `#/catalog` link keeps working. */
function shelfPath(sort: SortMode, group: GroupMode): string {
  const q = new URLSearchParams();
  if (sort !== DEFAULT_SORT) q.set("sort", sort);
  if (group !== DEFAULT_GROUP) q.set("group", group);
  const search = q.toString();
  return search ? `/catalog?${search}` : "/catalog";
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function Catalog({ query }: ScreenProps): ReactNode {
  const { library, status, error, reload } = useLibrary();

  const sort = readMode<SortMode>(query.get("sort"), SORT_LABELS, DEFAULT_SORT);
  const group = readMode<GroupMode>(query.get("group"), GROUP_LABELS, DEFAULT_GROUP);

  const games = useMemo(() => summariseGames(library), [library]);
  const groups = useMemo(() => groupGames(games, group, sort), [games, group, sort]);

  const controls = (
    <div className="pb-shelf__controls">
      <Segmented
        label="Sort"
        value={sort}
        options={SORT_OPTIONS}
        onChange={(next) => navigate(shelfPath(next, group), { replace: true })}
      />
      <Segmented
        label="Group"
        value={group}
        options={GROUP_OPTIONS}
        onChange={(next) => navigate(shelfPath(sort, next), { replace: true })}
      />
    </div>
  );

  // A running index across every group, so exactly one image opts out of lazy
  // loading no matter how the grid happens to wrap at this width.
  let tileIndex = 0;

  return (
    <div className="pb-stack">
      <ScreenHeader
        title="The shelf"
        back={{ to: "/", label: "Table" }}
        action={games.length > 0 ? <span className="pb-label">{countLabel(games.length)}</span> : undefined}
      />
      <SyncLine />

      {status === "error" ? (
        <EmptyState
          title="The shelf would not open"
          action={
            <button type="button" className="pb-btn" onClick={reload}>
              Try again
            </button>
          }
        >
          <p>{error ?? "The local store could not be read."}</p>
        </EmptyState>
      ) : status === "loading" && games.length === 0 ? (
        <p className="pb-shelf__note" role="status">
          Reading the shelf&hellip;
        </p>
      ) : games.length === 0 ? (
        <EmptyState
          title="Nothing on the shelf yet"
          action={
            <Link to="/" className="pb-btn pb-shelf__cta">
              Rate a game
            </Link>
          }
        >
          <p>
            The shelf fills itself: every game the group finishes a review of turns up here, box
            and all.
          </p>
        </EmptyState>
      ) : (
        <>
          {controls}
          {groups.map((section) => (
            <section className="pb-shelf__section" key={section.key}>
              {group !== "none" && (
                <>
                  <h2 className="pb-shelf__head">
                    <span className="pb-shelf__heading">{section.label}</span>
                    <span className="pb-shelf__count">{section.games.length}</span>
                  </h2>
                  {/* The hint exists so "Weight" is not read as the group's own
                      Weight aspect. Dropping it would recreate that confusion. */}
                  {section.hint && <p className="pb-shelf__hint">{section.hint}</p>}
                </>
              )}
              <ul className="pb-shelf__grid">
                {section.games.map((summary) => (
                  <li className="pb-shelf__item" key={summary.game.id}>
                    <ShelfTile summary={summary} eager={tileIndex++ === 0} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      )}

      <Attribution />
    </div>
  );
}

// ---------------------------------------------------------------------------
// One box on the shelf
// ---------------------------------------------------------------------------

/**
 * Title and meta are rendered as children rather than through `Tile`'s `title`
 * and `meta` props, because `Tile` puts those above its children and on this
 * screen the art has to come first. The classes are the same ones, so the type
 * still matches every other tile in the app.
 */
function ShelfTile({ summary, eager }: { summary: GameSummary; eager: boolean }): ReactNode {
  const { game, plays, lastPlayedAt } = summary;

  return (
    <Tile as={Link} to={`/game/${game.id}`} interactive className="pb-shelf__tile">
      <div className="pb-shelf__art">
        {/* 176px is the widest cell this grid produces; the CDN is asked for 2x
            that, and the CSS lets the reserved square fill whatever cell it got. */}
        <BoxArt game={game} size={176} eager={eager} />
      </div>
      <div className="pb-tile__title pb-shelf__name">{game.title}</div>
      <div className="pb-tile__meta">{playsLine(plays, lastPlayedAt)}</div>
      {/* Nights, not raters: both figures are already equal-weighted per session. */}
      <ScorePair headline={summary.headline} overall={summary.overall} unit="night" />
    </Tile>
  );
}

function playsLine(plays: number, lastPlayedAt: number | null): string {
  if (plays === 0 || lastPlayedAt === null) return "Never played";
  return `${plays} ${plays === 1 ? "play" : "plays"} · ${relativeDay(lastPlayedAt)}`;
}

function countLabel(n: number): string {
  return `${n} ${n === 1 ? "game" : "games"}`;
}
